import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import classes from "./PriceConfigurations.module.css";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import api from "../../utils/axios";
import { ColDef, RowValueChangedEvent } from "ag-grid-community";
import NumericEditor from "../../components/NumericEditor";
import { toast } from "react-toastify";
import ModalSelector from "../../components/form/ModalSelector";
import useLang from "../../hooks/useLang";
import { CONFIG } from "../../config";

type DriverPaymentDefaults = {
	id: number;
	from?: number;
	to?: number;
	price?: number;
	number?: number;
	countryId?: number;
};

type OtherCountriesPackageDefaults = {
	countryId: number;
	country: string;
	data: DriverPaymentDefaults[];
};
type Driver = {
	id: number;
	firstName?: string;
	lastName?: string;
};

const DriverPayments = () => {
	const [columns, setColumns] = useState<ColDef[]>();
	const [changedDriverPayments, setChangedDriverPayments] =
		useState<ColDef[]>();
	const [otherCountriesPackageDefaults, setOtherCountriesPackageDefaults] =
		useState<OtherCountriesPackageDefaults[]>();
	const [driverPaymentDefaults, setDriverPaymentDefaults] = useState<
		DriverPaymentDefaults[]
	>([]);

	const [editedDriverPaymentDefaults, setEditedDriverPaymentDefaults] =
		useState<DriverPaymentDefaults[]>([]);

	const [drivers, setDrivers] = useState<Driver[]>([]);

	const [driver, setDriver] = useState<Driver>();

	const [editedDriverPaymentData, seteditedDriverPaymentData] = useState<any>(
		[]
	);
	const selectEditedDriverPayments = async () => {
		try {
			const response = await api.get(
				"DriverPaymentDefaults/changed-driver-payments"
			);
			if (response.status === 200 && response.data) {
				seteditedDriverPaymentData(response.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
			autoHeaderHeight: true,
		}),
		[]
	);
	const { strings } = useLang("driverPayments");
	let grid = useRef(null);

	const getDriverPaymentDefaults = async (driver?: Driver) => {
		try {
			const response = await api.get("driverpaymentdefaults/list", {
				params: { driverId: driver?.id },
			});
			setDriverPaymentDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};

	const getOtherCountriesDriverPaymentDefaults = async (driver?: Driver) => {
		try {
			const response = await api.get(
				"driverpaymentdefaults/other-countries-list",
				{
					params: {
						driverId: driver?.id,
					},
				}
			);
			setOtherCountriesPackageDefaults(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};
	const getDrivers = async () => {
		try {
			const response = await api.get("user/drivers-list");
			setDrivers(response.data);
		} catch (error: any) {
			console.log(error);
		}
	};
	const getDefaults = async (driver?: Driver) => {
		await getDriverPaymentDefaults(driver);
		await getOtherCountriesDriverPaymentDefaults(driver);
	};

	useEffect(() => {
		getDefaults();
	}, []);

	useEffect(() => {
		getDrivers();
	}, []);

	useEffect(() => {
		const columns = [
			// {
			// 	field: "id",
			// 	headerName: "Id",
			// 	width: 80,
			// },
			{
				field: "from",
				headerName: strings?.columnDefs.from,
				width: 100,
			},
			{
				field: "to",
				headerName: strings?.columnDefs.to,
				width: 100,
			},
			{
				field: "price",
				headerName: strings?.columnDefs.price,
				width: 130,
				cellEditor: NumericEditor,
				cellEditorParams: {
					suffix: CONFIG?.CURRENCY,
				},
				cellStyle: {
					height: "100%",
				},
				editable: true,
			},
		];
		setChangedDriverPayments([
			{
				field: "from",
				headerName: strings?.columnDefs.from,
				width: 120,
				valueGetter: (params: any) => {
					return params.data.from
						? `${CONFIG?.CURRENCY} ${params.data.from}`
						: "- " + CONFIG?.CURRENCY;
				},
			},
			{
				field: "to",
				headerName: strings?.columnDefs.to,
				width: 120,
				valueGetter: (params: any) => {
					return params.data.to
						? `${CONFIG?.CURRENCY} ${params.data.to}`
						: "- " + CONFIG?.CURRENCY;
				},
			},
			{
				field: "price",
				headerName: strings?.columnDefs.price,
				width: 130,
				valueGetter: (params: any) => {
					return params.data.price
						? `${CONFIG?.CURRENCY} ${params.data.price}`
						: "- " + CONFIG?.CURRENCY;
				},
			},
			{
				field: "driverNameAndSurname",
				headerName: strings?.columnDefs.to,
				width: 150,
			},
		]);

		setColumns(columns);
		selectEditedDriverPayments();
	}, [setColumns, strings]);

	const updateArray = (prev: any[], data: any) => {
		if (prev.some((v) => v.id === data.id)) {
			return prev.map((v) => (v.id === data.id ? data : v));
		} else {
			return [...prev, data];
		}
	};

	const onValueRowValueChanged = (params: RowValueChangedEvent) => {
		setEditedDriverPaymentDefaults((prev) =>
			updateArray(prev, params.data)
		);
	};

	const onSubmit = async () => {
		try {
			await api.post("driverpaymentdefaults/update", {
				data: editedDriverPaymentDefaults.map((v) => ({
					id: v.id,
					price: v.price,
					number: v.number,
					countryId: v.countryId,
				})),
				driverId: driver?.id,
			});
			setEditedDriverPaymentDefaults([]);
			toast.success(strings?.toastMessages.updateSuccess);
			await getDefaults(driver);
		} catch (e) {
			console.log(e);
		}
	};

	const onDriverSelected = async (row: Driver) => {
		setDriver(row);
		await getDefaults(row);
	};

	const onRowDeleted = async () => {
		setDriver(undefined);
		setEditedDriverPaymentDefaults([]);
		await getDefaults();
	};

	return (
		<>
			<div className={classes.mainContainer}>
				<h1 className={classes.title}>{strings?.headers.mainTitle}</h1>
				<div>
					<ModalSelector
						buttonLabel={strings?.buttons.driverLabel}
						tableData={drivers}
						onRowSelected={onDriverSelected}
						onRowDeleted={onRowDeleted}
						columnsToShow={["firstName", "lastName"]}
					/>
				</div>
				<div
					className={"ag-theme-alpine"}
					style={{
						width: "100%",
						height: 400,
					}}
				>
					<AgGridReact
						rowData={driverPaymentDefaults}
						columnDefs={columns}
						defaultColDef={defaultColDef}
						defaultExcelExportParams={defaultExcelExportSettings}
						editType={"fullRow"}
						ref={grid}
						rowHeight={60}
						onRowValueChanged={onValueRowValueChanged}
					/>
				</div>
				<div className={classes.thirdContainer}>
					<h1 className={classes.title}>
						{"Pagesat e konfiguruara te shofereve"}
					</h1>
					<div
						className={"ag-theme-alpine"}
						style={{
							width: "100%",
							height: 400,
						}}
					>
						<AgGridReact
							rowData={editedDriverPaymentData}
							columnDefs={changedDriverPayments}
							defaultColDef={defaultColDef}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							editType={"fullRow"}
							ref={grid}
							rowHeight={60}
							onRowValueChanged={onValueRowValueChanged}
						/>
					</div>
				</div>
			</div>
			<div className="flex justify-end">
				<Button
					type="primary"
					size="large"
					// className="bg-blue-700"
					style={{ backgroundColor: "#4F16C2" }}
					onClick={onSubmit}
				>
					{strings?.buttons.saveButton}
				</Button>
			</div>
		</>
	);
};

export default DriverPayments;
